import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import DashboardCreateSelectNetworkModal from "../modal/SelectNetworks";
import { Device, NetworkSelected, NetworkType } from "types";
import SelectedList, { TableColumn } from "./SelectedList";
import Icon from "@mui/material/Icon";
import {
    CHART_ERROR_MESSAGE,
    CHART_NAME_PATTERN,
    ColorPicker,
    CreateChartState,
    useCreateDashboardStore,
} from "..";
import { ChartSource, ChartType } from "constant/chart";
import {
    BULK_CREATE_WIDGET_MESSAGE,
    DASHBOARD_CHART_MAX,
} from "constant";
import { ColorPickerBackground } from "components/common";
import { getWidgetConfigByWidgetType, WidgetType } from "components/dashboard/utils/widgetConfig";
import { getMQTTQueryByDevice } from "components/dashboard/utils/widgetUtil";

const CreateData: CreateChartState = {
    title: "Creating New Widget(s)",
    data: [],
    columns: [
        {
            key: "widgetName",
            name: "Widget Name",
        },
        {
            key: "deviceName",
            name: "Room",
        },
        {
            key: "gatewayName",
            name: "Network Name",
        },
        {
            key: "gatewayId",
            name: "Network ID",
        },
        {
            key: "color",
            name: "Colors",
            render: (data: any) => (
                <div className="color-picker__toggle justify-content-start gap-1">
                    <ColorPickerBackground
                        $inputColor={data.color}
                    />
                </div>
            ),
        },
    ],
    transform: (data: NetworkSelected) => ({
        attributes: [{
            color: data.color,
            device_id: data.ldsuUID,
            gateway_id: data.gatewayId,
            said: 0,
            name: data.deviceName,
            time_series: getMQTTQueryByDevice(data),
        }
        ],
        source: ChartSource.MULTIPLE_SENSORS,
        name: data.widgetName,
        chart: ChartType.BAR,
    }),
    validate() {
        return this.data.map((item) => ({
            path: `[name="${item.id}.widgetName"]`,
            isValid: CHART_NAME_PATTERN.test(item.widgetName.trim()),
            message: CHART_ERROR_MESSAGE,
        }));
    },
};

export const Columns: TableColumn<NetworkSelected>[] = [
    { key: "deviceName", label: "Room Name", className: "text-truncate col-2" },
    {
        key: "widgetName",
        label: "Widget Name",
        className: "col-2",
        render: (item, props) => (
            <Form.Control
                name="widget-name"
                type="text"
                placeholder="Widget Name"
                value={item.widgetName}
                onChange={(event) =>
                    props.onChange?.(item, { widgetName: event.target.value })
                }
            />
        ),
    },
    { key: "gatewayName", label: "Network Name" },
    { key: "gatewayId", label: "Network ID", className: "text-truncate col-2" },
    {
        key: "color",
        label: "Colors",
        render: (item, props) => (
            <div className="d-flex align-items-center">
                <ColorPicker
                    defaultValue={item.color}
                    onChange={(e, color) =>
                        props.onChange?.(item, {
                            color: color,
                        })
                    }
                />
            </div>
        ),
    },
    {
        key: "actions",
        label: "",
        render: (item, props) => (
            <Icon
                role="button"
                aria-label="remove-icon"
                className="cursor-pointer mr-2 text-primary-red-1"
                onClick={() => props.onRemove(item)}
            >
                close
            </Icon>
        ),
        className: "col-1 text-end",
    },
];

const CreateSingleUsageWidget = () => {
    const { widget } = useCreateDashboardStore();
    const widgetType = widget?.widgetType ?? WidgetType.PSL_CHART_SINGLE_ROOM;
    const widgetConfiguration = getWidgetConfigByWidgetType(widgetType);
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [selectedNetworks, setSelectedNetworks] = useState<
        Record<string, NetworkSelected>
    >({});

    useEffect(() => {
        useCreateDashboardStore.setState({
            createData: {
                ...CreateData,
                data: Object.values(selectedNetworks),
            },
        });
    }, [selectedNetworks, widgetConfiguration]);

    const onSelectNetwork = (networks: Record<string, Device>) => {
        const selected: Record<string, NetworkSelected> = Object.values(
            networks
        )
            .map((item: NetworkSelected, index: number) => ({
                ...item,
                widgetName: item.deviceName,
                color: "#F2994A", // assign color
            }))
            .pluck("deviceId");
        setSelectedNetworks(selected);
    };

    const onRemove = (device: NetworkSelected) => {
        const selected = { ...selectedNetworks };
        delete selected[device.deviceId];
        setSelectedNetworks(selected);
    };

    const onChange = (
        network: NetworkSelected,
        changes: Partial<NetworkSelected>
    ) => {
        const networks_ = { ...selectedNetworks };
        const networkChanges_ = { ...network, ...changes };
        networks_[network.deviceId] = networkChanges_;
        setSelectedNetworks(networks_);
    };

    const filterDevices = (device: Device) => {
        return device.deviceType === NetworkType.ROOM;
    }

    const networks = Object.values(selectedNetworks);

    return (
        <>
            <Row>
                <Col sm="12">
                    <div className="d-flex align-items-center gap-3">
                        <Button
                            variant="primary"
                            className="mr-2"
                            onClick={() => setShowSelectModal(true)}
                        >
                            Select Rooms
                        </Button>
                    </div>
                    {!widgetConfiguration.isCombined && (
                        <p className="reusable-description mt-2">
                            {BULK_CREATE_WIDGET_MESSAGE.fill({
                                type: "rooms",
                            })}
                        </p>
                    )}
                    <DashboardCreateSelectNetworkModal
                        show={showSelectModal}
                        maxSelected={widgetConfiguration.maxItems ?? DASHBOARD_CHART_MAX}
                        onHide={() => setShowSelectModal(false)}
                        selected={selectedNetworks}
                        onSave={(event, data) => {
                            onSelectNetwork(data);
                        }}
                        networkType={NetworkType.ROOM}
                        filter={filterDevices}
                    />
                </Col>
            </Row>
            <Row className="mt-4">
                {networks.length > 0 && (
                    <SelectedList
                        data={networks}
                        onRemove={onRemove}
                        onChange={onChange}
                        columns={Columns}
                    />
                )}
            </Row>
        </>
    );
};

export default React.memo(CreateSingleUsageWidget);
